import { createContext, useContext, useMemo, type ReactNode } from 'react';
import { strings as en, plurals as enPlurals } from './locales/en';
import { strings as de, plurals as dePlurals } from './locales/de';
import { strings as deCH, plurals as deCHPlurals } from './locales/de-CH';
import { strings as fr, plurals as frPlurals } from './locales/fr';
import { strings as it, plurals as itPlurals } from './locales/it';
import { strings as nl, plurals as nlPlurals } from './locales/nl';
import { strings as nlBE, plurals as nlBEPlurals } from './locales/nl-BE';
import { strings as ro, plurals as roPlurals } from './locales/ro';
import { strings as sl, plurals as slPlurals } from './locales/sl';

export type StringKey = keyof typeof en;
export type PluralKey = keyof typeof enPlurals;

/** Every language must supply every key, so a missing translation is a build error. */
export type Strings = Record<StringKey, string>;
/** Regional variants override only what actually differs from the language they extend. */
export type StringOverrides = Partial<Strings>;

type PluralForms = { other: string } & Partial<Record<Intl.LDMLPluralRule, string>>;
export type Plurals = Record<PluralKey, PluralForms>;
export type PluralOverrides = Partial<Plurals>;

export const LOCALES = [
  { code: 'en', label: 'English' },
  { code: 'de', label: 'Deutsch' },
  { code: 'de-CH', label: 'Schweizer Deutsch' },
  { code: 'fr', label: 'Français' },
  { code: 'it', label: 'Italiano' },
  { code: 'nl', label: 'Nederlands' },
  { code: 'nl-BE', label: 'Vlaams' },
  { code: 'ro', label: 'Română' },
  { code: 'sl', label: 'Slovenščina' },
] as const;

export type Locale = (typeof LOCALES)[number]['code'];

const LOCALE_CODES = LOCALES.map((entry) => entry.code) as readonly string[];

// Swiss German and Flemish are variants: they start from German and Dutch and replace only
// the phrases that genuinely differ, so the two never drift apart on everything else.
const STRINGS: Record<Locale, Strings> = {
  en,
  de,
  'de-CH': { ...de, ...(deCH as StringOverrides) },
  fr,
  it,
  nl,
  'nl-BE': { ...nl, ...(nlBE as StringOverrides) },
  ro,
  sl,
};

const PLURALS: Record<Locale, Plurals> = {
  en: enPlurals,
  de: dePlurals,
  'de-CH': { ...dePlurals, ...(deCHPlurals as PluralOverrides) },
  fr: frPlurals,
  it: itPlurals,
  nl: nlPlurals,
  'nl-BE': { ...nlPlurals, ...(nlBEPlurals as PluralOverrides) },
  ro: roPlurals,
  sl: slPlurals,
};

function fill(text: string, vars?: Record<string, string | number>): string {
  if (!vars) return text;
  return Object.entries(vars).reduce(
    (result, [name, value]) => result.split(`{${name}}`).join(String(value)),
    text,
  );
}

export function translate(locale: Locale, key: StringKey, vars?: Record<string, string | number>): string {
  return fill(STRINGS[locale]?.[key] ?? en[key], vars);
}

/** Picks the grammatically correct form for `count` using the browser's own plural rules. */
export function translatePlural(locale: Locale, key: PluralKey, count: number, vars?: Record<string, string | number>): string {
  const forms = PLURALS[locale]?.[key] ?? enPlurals[key];
  let rule: Intl.LDMLPluralRule = 'other';
  try {
    rule = new Intl.PluralRules(locale).select(count);
  } catch {
    // An unknown locale falls back to the `other` form rather than failing.
  }
  return fill(forms[rule] ?? forms.other, { count, ...vars });
}

export function isLocale(value: unknown): value is Locale {
  return typeof value === 'string' && LOCALE_CODES.includes(value);
}

/**
 * First run has no saved choice, and the language picker lives behind the unlock screen,
 * so start from what the browser reports. An exact regional match wins (nl-BE, de-CH),
 * otherwise the base language does.
 */
export function detectLocale(): Locale {
  const preferences = typeof navigator === 'undefined'
    ? []
    : navigator.languages?.length ? navigator.languages : [navigator.language];
  for (const preference of preferences) {
    if (!preference) continue;
    const wanted = preference.toLowerCase();
    const exact = LOCALES.find((entry) => entry.code.toLowerCase() === wanted);
    if (exact) return exact.code;
    const base = wanted.split('-')[0];
    const partial = LOCALES.find((entry) => entry.code.toLowerCase() === base);
    if (partial) return partial.code;
  }
  return 'en';
}

type LocaleContextValue = { locale: Locale; setLocale: (locale: Locale) => void };

const LocaleContext = createContext<LocaleContextValue>({ locale: 'en', setLocale: () => undefined });

export function LocaleProvider({ locale, onChange, children }: { locale: Locale; onChange: (locale: Locale) => void; children: ReactNode }) {
  const value = useMemo(() => ({ locale, setLocale: onChange }), [locale, onChange]);
  return <LocaleContext.Provider value={value}>{children}</LocaleContext.Provider>;
}

export function useT() {
  const { locale } = useContext(LocaleContext);
  return useMemo(() => ({
    locale,
    t: (key: StringKey, vars?: Record<string, string | number>) => translate(locale, key, vars),
    plural: (key: PluralKey, count: number, vars?: Record<string, string | number>) => translatePlural(locale, key, count, vars),
  }), [locale]);
}

export function useLocale() {
  return useContext(LocaleContext);
}

/**
 * Shown on the screens before unlock, where the wallet menu does not exist yet. Someone who
 * cannot read the detected language still needs a way to change it.
 */
export function LanguagePicker({ className = 'language-picker' }: { className?: string }) {
  const { locale, setLocale } = useLocale();
  return (
    <label className={className}>
      <span className="sr-only">{translate(locale, 'menu.language')}</span>
      <select
        aria-label={translate(locale, 'menu.language')}
        value={locale}
        onChange={(event) => setLocale(event.target.value as Locale)}
      >
        {LOCALES.map((entry) => <option key={entry.code} value={entry.code}>{entry.label}</option>)}
      </select>
    </label>
  );
}
